import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Upload,
  Modal,
  Typography,
  Divider,
  Space
} from "antd";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import CulsightPageLoader from "../../../User-panel/components/CulsightPageLoader";
import { EDIT_LEARNER, VIEW_PROFILE, RESET_PASSWORD } from "../../apis/apis";
import { UploadOutlined } from "@ant-design/icons";
import LmsCountryDropdown from "../../../User-panel/components/LmsCountryDropdown";
import { logout } from "../../../authService";

const { Title, Text } = Typography;

function Settings() {
  const { user, setUser } = useOutletContext();
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);

  const [first_name, set_first_name] = useState("");
  const [last_name, set_last_name] = useState("");
  const [email, set_email] = useState("");
  const [contact_no, set_contact_no] = useState("");
  const [image, set_image] = useState("");
  const [image_file, set_image_file] = useState(null);
  const [country_code, set_country_code] = useState("IN");
  const [imageError, setImageError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, set_error] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  };

  const props = {
    name: "avatar",
    accept: ".jpg,.jpeg,.png",
    showUploadList: false,
    beforeUpload: (file) => {
      const isValidType =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/jpg";

      if (!isValidType) {
        setImageError("Only JPG/PNG files are allowed.");
        return Upload.LIST_IGNORE;
      }

      const isLt500KB = file.size <= 512000;
      if (!isLt500KB) {
        setImageError("Image must be 500KB or smaller.");
        return Upload.LIST_IGNORE;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const { width, height } = img;
        if (width === 600 && height === 400) {
          setImageError("");
          getBase64(file, (url) => set_image(url));
          set_image_file(file);
        } else {
          setImageError("Image must be exactly 600x400 pixels.");
        }
      };

      return false;
    },
  };

  const fetchProfile = async () => {
    setLoading(true);
    const FORM_DATA = new FormData();
    const EDIT_API_RESPONSE = await VIEW_PROFILE(FORM_DATA);

    if (EDIT_API_RESPONSE?.data?.status) {
      const response_data = EDIT_API_RESPONSE?.data?.data;
      set_first_name(response_data?.first_name);
      set_last_name(response_data?.last_name);
      set_email(response_data?.email);
      set_contact_no(parseInt(response_data?.contact_no));
      set_country_code(response_data?.country_code);
      set_image(response_data?.image || "");

      setUser((prev) => ({
        ...prev,
        user_info: {
          ...prev?.user_info,
          name: `${response_data?.first_name} ${response_data?.last_name}`,
          email: response_data?.email,
          image: response_data?.image,
        },
      }));

      const updatedUser = {
        ...user,
        user_info: {
          ...user?.user_info,
          name: `${response_data?.first_name} ${response_data?.last_name}`,
          email: response_data?.email,
          image: response_data?.image,
        },
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const onFinish = async () => {
    setLoading(true);
    const FORM_DATA = new FormData();
    FORM_DATA.append("first_name", first_name);
    FORM_DATA.append("last_name", last_name);
    FORM_DATA.append("contact_no", contact_no);
    FORM_DATA.append("country_code", country_code);
    if (image_file) FORM_DATA.append("image", image_file);

    try {
      const response = await EDIT_LEARNER(FORM_DATA);
      if (response?.data?.status) {
        notification.success({
          message: "Successful",
          description: response?.data?.message,
        });
        await fetchProfile();
      } else {
        notification.error({
          message: "Error",
          description: response?.data?.message,
        });
        setLoading(false);
      }
    } catch (error) {
      message.error(
        "Server Error: " + (error?.response?.data?.message || "Unknown error")
      );
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    set_error({ new_password: "", confirm_password: "" });
    setPasswordLoading(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("email", email);
    FORM_DATA.append("current_password", currentPassword);
    FORM_DATA.append("new_password", newPassword);
    FORM_DATA.append("confirm_password", confirmPassword);

    try {
      const response = await RESET_PASSWORD(FORM_DATA);

      if (response?.data?.status) {
        notification.success({
          message: "Successfull",
          description: response?.data?.message
        });

        setIsModalOpen(false);
        setNewPassword("");
        setConfirmPassword("");
        setCurrentPassword("");
      } else {
        const errors = response?.data?.errors || {};
        set_error(errors);

        notification.error({
          message: "Error",
          description: Object.values(errors).join(" | "),
        });
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="lms-body" style={{ padding: 16 }}>
      <Card bordered={false}>
        {loading ? (
          <CulsightPageLoader />
        ) : (
          <>
            {/* HEADER */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  Profile Settings
                </Title>
                <Text type="secondary">Manage your account details</Text>
              </Col>

              <Button type="primary" onClick={() => setIsModalOpen(true)}>
                Reset Password
              </Button>
            </Row>

            <Divider />

            {/* PROFILE */}
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} md={6} style={{ textAlign: "center" }}>
                <Space direction="vertical" align="center">
                   {image ? (
                        <img
                          src={image}
                          alt="Profile Pic"
                          style={{
                            width: "120px",
                            height: "120px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            marginBottom: "10px"
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "120px",
                            height: "120px",
                            backgroundColor: "#ccc",
                            borderRadius: "50%",
                            marginBottom: "10px"
                          }}
                        />
                      )}

                  <Upload {...props}>
                    <Button icon={<UploadOutlined />}>
                      Change Photo
                    </Button>
                  </Upload>

                  {imageError && <Text type="danger">{imageError}</Text>}
                </Space>
              </Col>

              <Col xs={24} md={18}>
                <Title level={5}>
                  {first_name} {last_name}
                </Title>

                <Form layout="vertical" onFinish={onFinish}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="First Name">
                        <Input
                          value={first_name}
                          onChange={(e) => set_first_name(e.target.value)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item label="Last Name">
                        <Input
                          value={last_name}
                          onChange={(e) => set_last_name(e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label="Email">
                    <Input value={email} disabled />
                  </Form.Item>

                  <Form.Item label="Contact Number">
                    <LmsCountryDropdown
                      country={country_code}
                      contact={contact_no}
                      set_country_code={set_country_code}
                      set_contact_no={set_contact_no}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      Save Changes
                    </Button>
                  </Form.Item>
                </Form>
              </Col>
            </Row>
          </>
        )}
      </Card>

      {/* MODAL */}
      <Modal
        title="Reset Password"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Form layout="vertical" onFinish={handlePasswordSubmit}>
          <Form.Item label="Current Password">
            <Input.Password
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <Text type="danger">{error.current_password}</Text>
          </Form.Item>

          <Form.Item label="New Password">
            <Input.Password
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Text type="danger">{error.new_password}</Text>
          </Form.Item>

          <Form.Item label="Confirm Password">
            <Input.Password
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Text type="danger">{error.confirm_password}</Text>
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={passwordLoading}>
            Update Password
          </Button>
        </Form>
      </Modal>
    </div>
  );
}

export default Settings;